/**
 * The doUserAgentMatch function takes a string as an argument and returns true if the user agent matches that string.
 * @param searchStr Search for the user agent string
 *
 * @return {boolean} A boolean value
 *
 */
const doUserAgentMatch = (searchStr) => {
  const ua = navigator.userAgent.toLowerCase(); // Convert user agent to lowercase for case-insensitive matching
  const regexp = new RegExp(searchStr.toLowerCase(), 'i'); // Create a case-insensitive regular expression
  return regexp.test(ua);
};

/**
 * The isTouchDevice function returns a boolean value indicating whether the user's device is touch-enabled.
 * @return {boolean} True if the device is a touch device, false otherwise
 */
const isTouchDevice = () =>
  window.matchMedia('(hover: none)').matches ||
  window.matchMedia('(pointer: touch)').matches ||
  navigator.maxTouchPoints > 0 ||
  'ontouchstart' in window;

/**
 * The isIos function checks if the user is on an iOS device.
 * @return {boolean} True if the user agent is an iphone, ipad or mac and it is a touch device
 */
export const isIos = () =>
  (doUserAgentMatch('iPhone') || doUserAgentMatch('iPad') || doUserAgentMatch('Mac')) && isTouchDevice();

/**
 * The isMobile function returns true if the user is on a mobile device.
 * @return {boolean} True if the device is a mobile device, false otherwise
 */
export const isMobile = () => {
  const hasCursor = window.matchMedia('(pointer:fine)').matches;
  const hasOrientation = 'orientation' in window;

  let isMobileDevice =
    isTouchDevice() && !hasCursor && hasOrientation && (isWidthDeviceMobileTablet() || isWidthDeviceMobile());

  if (!isMobileDevice) {
    if (
      (doUserAgentMatch('iPhone') || doUserAgentMatch('Android')) &&
      !doUserAgentMatch('Windows') &&
      (isWidthDeviceMobileTablet() || isWidthDeviceMobile())
    ) {
      isMobileDevice = true;
    }
  }
  return isMobileDevice;
};

// Mobile + Tablet
export const isWidthDeviceMobileTablet = () => {
  const windowWidth = window.innerWidth;
  return windowWidth < 1023;
};

// Mobile
export const isWidthDeviceMobile = () => {
  const windowWidth = window.innerWidth;
  return windowWidth < 768;
};

// Tablet
export const isWidthDeviceTablet = () => {
  const windowWidth = window.innerWidth;
  return windowWidth >= 768 && windowWidth < 1023;
};

/**
 * Checks if the current browser is Safari.
 *
 * @returns {boolean} - Returns true if the current browser is Safari, false otherwise.
 */
export const isSafari = () => {
  const checkSafariOnIos = () => doUserAgentMatch('AppleWebKit') && !doUserAgentMatch('CriOS');
  const checkSafariOnDesktop = () => Boolean(window.ApplePaySetupFeature) || Boolean(window.safari);
  return isIos() ? checkSafariOnIos() : checkSafariOnDesktop();
};
